/**
 * Top level: auth gate, navigation rail, and the single live stream everything else hangs off.
 *
 * One EventSource for the whole page, not one per panel. Connections are capped per account
 * (three by default) and a staff member with the log list, the voice tab and a player drawer open is
 * one person looking at one page — three connections for that would spend the whole budget on a
 * single browser window.
 *
 * The stream stays connected across tab changes. Only a filter change reconnects it, because only a
 * filter change alters what the server should be sending.
 */

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  APP_DOWNLOAD_URL,
  fetchAppInfo,
  fetchLogMeta,
  fetchLogs,
  fetchMe,
  fetchSecurity,
  logout,
  type LoginAlert,
} from "./api";
import {
  LoginAlertBanner,
  SecuredScreen,
  SecurityTab,
  notifyLoginAlert,
  type RejectOutcome,
} from "./components/SecurityTab";
import { useLiveStream } from "./hooks/useLiveStream";
import { useUrlFilters, type Filters } from "./hooks/useUrlFilters";
import { useSettings } from "./hooks/useSettings";
import { NARROW_QUERY, useMediaQuery } from "./hooks/useMediaQuery";
import { formatBytes, setTimeMode } from "./format";
import { compareVersions, detectClient } from "./client";
import { LogList, useLogBuffer } from "./components/LogList";
import { FilterBar } from "./components/FilterBar";
import { PlayerDrawer } from "./components/PlayerDrawer";
import { VoiceTab } from "./components/VoiceTab";
import { DiscordTab } from "./components/DiscordTab";
import { AdminTab } from "./components/AdminTab";
import { OverviewTab } from "./components/OverviewTab";
import { SettingsTab } from "./components/SettingsTab";
import { IconBriefcase, IconCage, IconChat, IconCube, IconDownload, IconGear, IconHome, IconList, IconLock, IconMap, IconMic, IconShield, IconSwords, IconUsers } from "./components/Icons";
import { PlayersTab } from "./components/PlayersTab";
import { DupeTab } from "./components/DupeTab";
import { SPAWNER_STASH, SpawnerStashModal, SpawnerStashTab } from "./components/SpawnerStash";
import { ChangePasswordForm, LoginForm } from "./components/LoginScreen";
import { PORTAL_SECTIONS, PortalTab } from "./components/PortalTab";
import { ProfileTab } from "./components/ProfileTab";
import { MapsTab } from "./components/MapsTab";
import { PROFILE_PREVIEW_USERS } from "./profileApi";
import { can, type AuthenticatedMe, type ConnectionState, type LogRow, type PunishmentEvent, type VoiceEvent } from "./types";

/** Admin only, so loaded on demand: other tiers never download it. (The server is what actually
 *  protects the data — every request it makes is refused for anyone else.) */
const WagerDebugTab = lazy(() =>
  import("./components/WagerDebugTab").then((module) => ({ default: module.WagerDebugTab })),
);

const CONNECTION_LABEL: Record<ConnectionState, string> = {
  live: "Live",
  reconnecting: "Reconnecting",
  "degraded-polling": "Degraded",
  offline: "Paused",
};

const PAGE_TITLES: Record<Filters["tab"], { title: string; blurb: string }> = {
  profile: { title: "Player profile", blurb: "Everything about one player — preview" },
  maps: { title: "Maps", blurb: "Every map on the network, and blocking" },
  home: { title: "Home", blurb: "Everything in the portal" },
  overview: { title: "Overview", blurb: "What is happening across the network right now" },
  players: { title: "Players", blurb: "Who is online right now, by proxy" },
  logs: { title: "Logs", blurb: "Every debug event, newest first" },
  dupe: { title: "Dupe log", blurb: "Duplicated items destroyed by the anti-dupe system" },
  stashes: { title: "Spawner stashes", blurb: "Containers holding 64 or more spawners, newest first" },
  wagerdebug: { title: "Wager Debug", blurb: "Every step of every wager fight, for finding out what went wrong" },
  voice: { title: "Voice moderation", blurb: "Flagged utterances, transcripts and clips" },
  discord: { title: "Discord logs", blurb: "Deleted messages, bans, timeouts, mutes and channel changes on the Discord server" },
  portal: { title: "Staff portal", blurb: "Leave of absence, punishments and evidence" },
  security: { title: "Account security", blurb: "Login alerts, and everywhere you're signed in" },
  admin: { title: "Users & audit", blurb: "Staff accounts, and who looked at what, and when" },
  settings: { title: "Settings", blurb: "Appearance and behaviour, stored in this browser" },
};

export function App(): JSX.Element {
  const me = useQuery({ queryKey: ["me"], queryFn: fetchMe, retry: false });

  if (me.isLoading) {
    return (
      <div className="login">
        <div className="login-card">Loading…</div>
      </div>
    );
  }

  // A failed /api/me is NOT the same as being signed out, and showing the login page for both is
  // misleading in the one case where it matters: if the server is unwell, "sign in" will not help and
  // sends people round a loop. This distinguishes them.
  if (me.isError) {
    return (
      <div className="login">
        <div className="login-card">
          <div className="rail-mark">!</div>
          <h1>Can&rsquo;t reach the dashboard</h1>
          <p>
            The server did not respond. This is not a sign-in problem — retry in a moment, and if it
            persists, check the service is running.
          </p>
          <a href="/" onClick={() => window.location.reload()}>
            Retry
          </a>
        </div>
      </div>
    );
  }

  if (!me.data?.authenticated) {
    return <LoginForm />;
  }

  const authed = me.data as AuthenticatedMe;
  if (authed.mustChangePassword) {
    return <ChangePasswordForm username={authed.user.username} csrfToken={authed.csrfToken} />;
  }

  return <Dashboard me={authed} />;
}

function Dashboard({ me }: { me: AuthenticatedMe }): JSX.Element {
  const { filters, setFilters, reset, logQuery } = useUrlFilters();
  const { settings, update, reset: resetSettings } = useSettings();
  const buffer = useLogBuffer();
  const listRef = useRef<HTMLDivElement | null>(null);

  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [voiceEvents, setVoiceEvents] = useState<VoiceEvent[]>([]);
  const [lastPunishment, setLastPunishment] = useState<PunishmentEvent | null>(null);
  const [queryMs, setQueryMs] = useState(0);
  /** Incremented on every live event, so the overview knows to refresh without polling. */
  const [liveTick, setLiveTick] = useState(0);
  const [liveDupeRows, setLiveDupeRows] = useState<LogRow[]>([]);
  const [liveStashRows, setLiveStashRows] = useState<LogRow[]>([]);
  /** The SPAWNER_STASH log row whose detail card is open, if any. */
  const [openStash, setOpenStash] = useState<LogRow | null>(null);
  /**
   * Bumped when something outside the filter bar sets its player filter. The bar keeps its own copy
   * of the typed text and would otherwise write the old text straight back; remounting it re-reads
   * the URL instead.
   */
  const [filterBarKey, setFilterBarKey] = useState(0);

  const tier = me.user.tier;
  const seesLogs = can(tier, "logs");

  // The Apple / CarPlay-style theme, released to everyone. Set to false to go back to the Minecraft theme.
  const apple = true;
  useEffect(() => {
    const root = document.documentElement;
    if (!apple) {
      delete root.dataset.theme;
      return;
    }
    root.dataset.theme = "apple";
    // The accent set in Settings is an inline style, which CSS cannot override, so the system blue
    // is applied here and the user's own accent put back when the theme is turned off.
    const previousAccent = root.style.getPropertyValue("--accent");
    const previousInk = root.style.getPropertyValue("--accent-ink");
    root.style.setProperty("--accent", "#0a84ff");
    root.style.setProperty("--accent-ink", "#ffffff");
    return () => {
      delete root.dataset.theme;
      root.style.setProperty("--accent", previousAccent);
      root.style.setProperty("--accent-ink", previousInk);
    };
  }, [apple, settings.accent]);
  const isAdmin = can(tier, "admin");

  // ---- login alerts -------------------------------------------------------------------------------
  // Pushed live over the stream when another session signs in from a new device, and also polled
  // once a minute so an alert that arrived while the stream was off still shows up.
  const queryClient = useQueryClient();
  const [secured, setSecured] = useState<RejectOutcome | null>(null);
  const [dismissedAlerts, setDismissedAlerts] = useState<number[]>([]);
  const security = useQuery({ queryKey: ["open-alerts"], queryFn: fetchSecurity, refetchInterval: 60_000 });
  const openAlerts = (security.data?.alerts ?? []).filter(
    (alert) => alert.status === "open" && !dismissedAlerts.includes(alert.id),
  );
  const onLoginAlert = useCallback(
    (alert: LoginAlert) => {
      notifyLoginAlert(alert);
      void queryClient.invalidateQueries({ queryKey: ["open-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["security"] });
    },
    [queryClient],
  );
  // App or browser: the page's own check, or what the server saw on /api/me.
  const client = useMemo(() => {
    const local = detectClient();
    return local.isApp ? local : (me.client ?? local);
  }, [me.client]);

  // Every signed-in account can download the app.
  const appInfo = useQuery({
    queryKey: ["app-info"],
    queryFn: fetchAppInfo,
    staleTime: 5 * 60_000,
  });
  const updateAvailable = Boolean(
    client.isApp &&
      client.version &&
      appInfo.data?.available &&
      appInfo.data.version &&
      compareVersions(appInfo.data.version, client.version) > 0,
  );
  const meta = useQuery({ queryKey: ["log-meta"], queryFn: fetchLogMeta, enabled: seesLogs });

  // Staff have no overview or logs; a link to one lands on the home screen instead.
  useEffect(() => {
    if (!seesLogs && ["overview", "logs", "dupe", "stashes", "wagerdebug"].includes(filters.tab)) {
      setFilters({ tab: "home" });
    } else if (!isAdmin && filters.tab === "wagerdebug") {
      // Same landing as an unrecognised tab, so the link does not hint that anything is there.
      setFilters({ tab: "home" });
    }
  }, [seesLogs, isAdmin, tier, filters.tab, setFilters]);

  // Timestamps are rendered from module state rather than a prop threaded everywhere; this keeps it
  // in step with the setting.
  useEffect(() => {
    setTimeMode(settings.timeMode);
  }, [settings.timeMode]);

  // ---- page one, refetched whenever the filters change ------------------------------------------
  //
  // A filter change discards the buffer outright rather than reconciling it. Rows already held may or
  // may not match the new filters, and deciding which is exactly the SQL the server already ran —
  // doing it again, differently, in the browser is how the two ends drift apart.
  const loadFirstPage = useCallback(async () => {
    if (!seesLogs) return;
    const page = await fetchLogs(logQuery, null);
    buffer.replaceAll(page.rows);
    setCursor(page.nextCursor);
    setHasMore(page.nextCursor !== null);
    setQueryMs(page.queryMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logQuery]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchLogs(logQuery, cursor);
      buffer.appendPage(page.rows);
      setCursor(page.nextCursor);
      setHasMore(page.nextCursor !== null);
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, loadingMore, logQuery]);

  // ---- the live stream --------------------------------------------------------------------------

  const streamQuery = useMemo(() => {
    const params = new URLSearchParams(logQuery);
    if (filters.drawer) params.set("watch", filters.drawer);
    return params.toString();
  }, [logQuery, filters.drawer]);

  const { state } = useLiveStream({
    query: streamQuery,
    enabled: filters.live,
    onLog: (row) => {
      buffer.pushLive(row);
      setLiveTick((tick) => tick + 1);
      // Kept separately from the log buffer: the Dupe tab wants to know an incident landed, and the
      // log buffer is capped and filtered for a different purpose.
      if (row.type === "DUPE") {
        setLiveDupeRows((current) => [row, ...current].slice(0, 50));
      }
      if (row.type === SPAWNER_STASH) {
        setLiveStashRows((current) => [row, ...current].slice(0, 50));
      }
    },
    onVoice: (event) => setVoiceEvents((current) => [event, ...current].slice(0, 500)),
    onPunishment: (event) => {
      setLastPunishment(event);
      setLiveTick((tick) => tick + 1);
    },
    // Continuity was broken. Rather than guess at what was missed, throw the buffer away and take a
    // clean page one — correct, and cheaper than reasoning about the hole.
    onGap: () => {
      void loadFirstPage();
    },
    onLoginAlert,
  });

  // ---- pause on scroll --------------------------------------------------------------------------
  //
  // Auto-scroll only while the user is at the very top. The moment they scroll down to read
  // something, live rows are held back and counted instead.
  useEffect(() => {
    if (filters.tab !== "logs") return;
    const element = document.querySelector<HTMLDivElement>(".list");
    if (!element) return;
    listRef.current = element;

    const onScroll = (): void => buffer.setPaused(element.scrollTop > 4);
    element.addEventListener("scroll", onScroll, { passive: true });
    return () => element.removeEventListener("scroll", onScroll);
  }, [buffer, filters.tab]);

  const resume = useCallback(() => {
    buffer.flushPending();
    buffer.setPaused(false);
    listRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [buffer]);

  // Without the logs permission there is no player drawer; a name opens the portal lookup instead.
  const openPlayer = useCallback(
    (player: string) =>
      setFilters(seesLogs ? { drawer: player } : { tab: "portal", section: "punishments", lookup: player }),
    [setFilters, seesLogs],
  );

  const go = useCallback(
    (next: Partial<Filters>) => {
      setFilters(next);
      setOpenStash(null);
    },
    [setFilters],
  );

  // Player profile: /players/<uuid>. Empty goes back to the profile search.
  const setProfile = useCallback((id: string) => setFilters({ tab: "profile", profile: id }), [setFilters]);

  // From a stash card: show that player's whole log. By name — the log filters have no uuid filter.
  const filterLogsByPlayer = useCallback(
    (player: string) => {
      setOpenStash(null);
      setFilters({ tab: "logs", player, types: [] });
      setFilterBarKey((key) => key + 1);
    },
    [setFilters],
  );

  // Inside a portal section, the header names the section rather than the portal.
  const openSection =
    filters.tab === "portal" ? PORTAL_SECTIONS.find((s) => s.id === filters.section) ?? null : null;
  const page = openSection ? { title: openSection.title, blurb: openSection.blurb } : PAGE_TITLES[filters.tab];
  // Rows stack onto two lines below the breakpoint, so they need roughly double the height. Density
  // is a desktop concern; on a phone the layout, not the preference, decides.
  const narrow = useMediaQuery(NARROW_QUERY);
  const rowHeight = narrow
    ? 44
    : settings.density === "compact"
      ? 24
      : settings.density === "roomy"
        ? 34
        : 28;

  // `group` starts a labelled section in the rail; items after it belong to it until the next label.
  const navItems: Array<{ tab: Filters["tab"]; label: string; icon: JSX.Element; show: boolean; group?: string }> = [
    { tab: "overview", label: "Overview", icon: <IconHome />, show: seesLogs },
    { tab: "players", label: "Players", icon: <IconUsers />, show: true },
    { tab: "logs", label: "Game logs", icon: <IconList />, show: seesLogs, group: "Logs" },
    { tab: "discord", label: "Discord logs", icon: <IconChat />, show: can(tier, "discord") },
    { tab: "voice", label: "Voice", icon: <IconMic />, show: can(tier, "voice") },
    { tab: "dupe", label: "Dupe log", icon: <IconCube />, show: seesLogs },
    { tab: "stashes", label: "Spawner stashes", icon: <IconCage />, show: seesLogs },
    { tab: "portal", label: "Staff portal", icon: <IconBriefcase />, show: can(tier, "portal"), group: "Staff" },
    { tab: "wagerdebug", label: "Wager debug", icon: <IconSwords />, show: isAdmin },
    { tab: "admin", label: "Users and audit", icon: <IconShield />, show: can(tier, "admin") },
    { tab: "security", label: "Account security", icon: <IconLock />, show: true, group: "Account" },
    { tab: "settings", label: "Settings", icon: <IconGear />, show: true },
    { tab: "maps", label: "Maps", icon: <IconMap />, show: true },
    // Preview: only the listed accounts (the server refuses everyone else as well).
    {
      tab: "profile",
      label: "Player profiles",
      icon: <IconUsers />,
      show: PROFILE_PREVIEW_USERS.includes(me.user.username.toLowerCase()),
      group: "Preview",
    },
  ];
  // Each item inherits the most recent `group`, so a section whose first item is hidden for this tier
  // (staff cannot see "Game logs") still gets its heading above whichever item is shown first.
  let currentGroup: string | undefined;
  const visibleNav = navItems
    .map((item) => {
      currentGroup = item.group ?? currentGroup;
      return { ...item, section: currentGroup };
    })
    .filter((item) => item.show);

  // After "That wasn't me": every session, this one included, is already gone server-side.
  if (secured) return <SecuredScreen outcome={secured} />;

  return (
    <div className="shell">
      {filters.tab === "home" ? (
        /* The home screen: every page this account can open, as big tiles. No sidebar anywhere. */
        <main className="home">
          <header className="home-head">
            <div className="home-brand">
              <span className="rail-mark">P</span>
              <div>
                <h1>PrismSMP Staff Portal</h1>
                <p>
                  Signed in as <b>{me.user.displayName}</b> <span className="home-tier" data-tier={tier}>{tier}</span>
                </p>
              </div>
            </div>
            <span className="grow" />
            <div
              className="client-badge"
              data-app={client.isApp}
              title={
                client.isApp
                  ? `PrismSMP Staff Portal app${client.version ? ` v${client.version}` : ""}${client.platform ? ` on ${client.platform}` : ""}`
                  : "You are using the website in a normal browser"
              }
            >
              <span className="client-icon" aria-hidden="true">
                {client.isApp ? "▣" : "◎"}
              </span>
              {client.isApp ? (
                <>
                  Desktop app
                  {client.version && <span className="client-version">v{client.version}</span>}
                </>
              ) : (
                "Web browser"
              )}
            </div>
            <span className="conn" data-state={state}>
              <span className="dot" />
              {CONNECTION_LABEL[state]}
            </span>
            <label className="switch">
              <input
                type="checkbox"
                checked={filters.live}
                onChange={(event) => setFilters({ live: event.target.checked })}
              />
              Live tail
            </label>
            <button
              type="button"
              className="btn"
              onClick={async () => {
                await logout(me.csrfToken);
                window.location.reload();
              }}
            >
              Sign out
            </button>
          </header>

          <div className="home-grid">
            {visibleNav.map((item, index) => (
              <button
                key={item.tab}
                type="button"
                className="home-tile"
                data-tile={item.tab}
                style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
                onClick={() => go({ tab: item.tab, section: "" })}
              >
                <span className="home-tile-icon" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="home-tile-label">{item.label}</span>
                {item.section && <span className="home-tile-group">{item.section}</span>}
                {item.tab === "logs" && buffer.pending.length > 0 && (
                  <span className="nav-count home-tile-count">{buffer.pending.length}</span>
                )}
                {item.tab === "security" && openAlerts.length > 0 && (
                  <span className="nav-count nav-count-alert home-tile-count">{openAlerts.length}</span>
                )}
              </button>
            ))}

            {/* In a browser: offer the app. In the app: offer it again only as an update. */}
            {appInfo.data?.available && (!client.isApp || updateAvailable) && (
              <a
                className="home-tile home-tile-download"
                data-update={updateAvailable}
                href={APP_DOWNLOAD_URL}
                download={appInfo.data.fileName}
                title={[
                  appInfo.data.fileName,
                  appInfo.data.version ? `version ${appInfo.data.version}` : null,
                  appInfo.data.bytes ? formatBytes(appInfo.data.bytes) : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              >
                <span className="home-tile-icon" aria-hidden="true">
                  <IconDownload />
                </span>
                <span className="home-tile-label">{updateAvailable ? "Update app" : "Download app"}</span>
                {appInfo.data.version && <span className="home-tile-group">v{appInfo.data.version}</span>}
              </a>
            )}
          </div>

          {openAlerts[0] && (
            <LoginAlertBanner
              key={openAlerts[0].id}
              alert={openAlerts[0]}
              csrfToken={me.csrfToken}
              onResolved={() => {
                setDismissedAlerts((ids) => [...ids, openAlerts[0]!.id]);
                void queryClient.invalidateQueries({ queryKey: ["open-alerts"] });
              }}
              onRejected={setSecured}
            />
          )}
        </main>
      ) : (
      <div className="content">
        <div className="page-head">
          <button
            type="button"
            className="btn home-btn"
            onClick={() => go({ tab: "home", section: "" })}
            aria-label="Back to the home screen"
          >
            ‹ Home
          </button>
          {/* Inside a portal section, a second step back: to the portal's own tiles. */}
          {openSection && (
            <button type="button" className="btn home-btn" onClick={() => go({ section: "" })}>
              ‹ Staff portal
            </button>
          )}
          <div>
            <h1>{page.title}</h1>
            <p>{page.blurb}</p>
          </div>
          <span className="grow" />
          {filters.tab === "logs" && (
            <span className="conn" data-state={state}>
              <span className="dot" />
              {CONNECTION_LABEL[state]}
            </span>
          )}
        </div>
        {/* Not on the security page itself, which lists the same alert with the same buttons. */}
        {filters.tab !== "security" && openAlerts[0] && (
          <LoginAlertBanner
            key={openAlerts[0].id}
            alert={openAlerts[0]}
            csrfToken={me.csrfToken}
            onResolved={() => {
              setDismissedAlerts((ids) => [...ids, openAlerts[0]!.id]);
              void queryClient.invalidateQueries({ queryKey: ["open-alerts"] });
            }}
            onRejected={setSecured}
          />
        )}

        {filters.tab === "overview" && (
          <OverviewTab
            tier={tier}
            connection={state}
            liveTick={liveTick}
            go={go}
            onOpenPlayer={openPlayer}
          />
        )}

        {filters.tab === "players" && <PlayersTab onOpenPlayer={openPlayer} />}

        {filters.tab === "logs" && (
          <>
            <FilterBar key={filterBarKey} filters={filters} setFilters={setFilters} reset={reset} meta={meta.data} />
            <LogList
              rows={buffer.rows}
              liveIds={buffer.liveIds}
              hasMore={hasMore}
              loading={loadingMore}
              onLoadMore={loadMore}
              onOpenPlayer={openPlayer}
              onOpenRow={setOpenStash}
              pendingCount={buffer.pending.length}
              onResume={resume}
              showServer={settings.showServer}
              flashNew={settings.flashNew}
              rowHeight={rowHeight}
            />
            <div className="statusline">
              <span>{buffer.rows.length.toLocaleString()} rows buffered</span>
              <span>query {queryMs}ms</span>
              {settings.timeMode === "local" && <span>times shown in local zone</span>}
              {buffer.paused && <span>auto-scroll paused</span>}
              <span className="grow" />
              <span>/ search · j/k move · esc close</span>
            </div>
          </>
        )}

        {filters.tab === "dupe" && (
          <DupeTab onOpenPlayer={openPlayer} liveRows={liveDupeRows} csrfToken={me.csrfToken} />
        )}

        {filters.tab === "stashes" && seesLogs && (
          <SpawnerStashTab
            liveRows={liveStashRows}
            onOpenPlayer={openPlayer}
            onFilterPlayer={filterLogsByPlayer}
          />
        )}

        {filters.tab === "wagerdebug" && isAdmin && (
          <Suspense fallback={<div className="empty">Loading…</div>}>
            <WagerDebugTab servers={meta.data?.servers ?? []} />
          </Suspense>
        )}

        {filters.tab === "voice" && can(tier, "voice") && (
          <VoiceTab
            liveEvents={voiceEvents}
            onOpenPlayer={openPlayer}
            focusEventId={filters.event}
            onClearFocus={() => setFilters({ event: "" })}
            csrfToken={me.csrfToken}
          />
        )}

        {filters.tab === "discord" && can(tier, "discord") && <DiscordTab />}

        {/* Its own scroll area: the portal stacks several tables plus evidence previews, which
            outgrow the window, and the page column itself does not scroll. */}
        {filters.tab === "portal" && can(tier, "portal") && (
          <div className="scroll pad">
            <PortalTab
              tier={tier}
              userId={me.user.id}
              csrfToken={me.csrfToken}
              lookup={filters.lookup}
              setLookup={(player) => setFilters({ lookup: player, section: "punishments" })}
              section={filters.section}
              setSection={(section) => setFilters({ section })}
            />
          </div>
        )}

        {filters.tab === "admin" && can(tier, "admin") && (
          <div className="scroll pad">
            <AdminTab onOpenPlayer={openPlayer} csrfToken={me.csrfToken} currentUserId={me.user.id} />
          </div>
        )}

        {filters.tab === "settings" && (
          <SettingsTab settings={settings} update={update} reset={resetSettings} tier={tier} />
        )}

        {filters.tab === "maps" && (
          <div className="scroll pad">
            <MapsTab
              tier={tier}
              csrfToken={me.csrfToken}
              onOpenPlayer={PROFILE_PREVIEW_USERS.includes(me.user.username.toLowerCase()) ? setProfile : undefined}
            />
          </div>
        )}

        {filters.tab === "profile" && PROFILE_PREVIEW_USERS.includes(me.user.username.toLowerCase()) && (
          <div className="scroll pad">
            <ProfileTab
              profile={filters.profile}
              setProfile={setProfile}
              tier={tier}
              csrfToken={me.csrfToken}
              lastPunishment={lastPunishment}
            />
          </div>
        )}

        {filters.tab === "security" && (
          <div className="scroll pad">
            <SecurityTab
              csrfToken={me.csrfToken}
              focusAlertId={filters.alert}
              onRejected={setSecured}
              onSignedOutSelf={() => window.location.assign("/")}
            />
          </div>
        )}
      </div>
      )}

      {openStash && filters.tab === "logs" && seesLogs && (
        <SpawnerStashModal
          id={openStash.id}
          fallback={openStash}
          onClose={() => setOpenStash(null)}
          onFilterPlayer={filterLogsByPlayer}
        />
      )}

      {filters.drawer && seesLogs && (
        <PlayerDrawer
          player={filters.drawer}
          tier={tier}
          onClose={() => setFilters({ drawer: "" })}
          onOpenPlayer={openPlayer}
          lastPunishmentEvent={lastPunishment}
        />
      )}
    </div>
  );
}
